import React from "react";
import { useSimulator } from "@/context/SimulatorContext";
import { Card } from "@/components/ui/card";
import { KpiCard } from "@/components/simulator/KpiCard";
import { fmtEUR, fmtPct, fmtNum } from "@/lib/format";
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";
import { TrendingUp, TrendingDown, Landmark, Target, Wallet } from "lucide-react";

export const OverviewTab = () => {
  const { summary, inputs } = useSimulator();

  const revenueData = [
    { name: "Zafferano - Stimmi", value: summary.saffron.rev_dried, color: "#fbbf24" },
    { name: "Zafferano - Estratto Std.", value: summary.saffron.rev_extract, color: "#f59e0b" },
    { name: "Zafferano - SC-CO₂", value: summary.saffron.rev_scco2, color: "#d97706" },
    { name: "Chlorella", value: summary.algae.chlorella.total, color: "#22c55e" },
    { name: "Spirulina", value: summary.algae.spirulina.total, color: "#14b8a6" },
    { name: "Scenedesmus", value: summary.algae.scenedesmus.total, color: "#84cc16" },
    { name: "Lotti Futuri", value: summary.futureLots.revenue, color: "#8b5cf6" },
  ].filter((d) => d.value > 0);

  const lotComparison = [
    { name: "Lotto 1 Zafferano", value: summary.saffron.total, fill: "#f59e0b" },
    { name: "Lotto 2 Microalghe", value: summary.algae.total, fill: "#10b981" },
    { name: "Lotti Futuri", value: summary.futureLots.revenue, fill: "#8b5cf6" },
    { name: "OPEX Annuo", value: summary.totalOpexAnnual, fill: "#f43f5e" },
    { name: "EBITDA", value: Math.max(0, summary.operatingMargin), fill: "#3b82f6" },
  ];

  const marginPositive = summary.operatingMargin >= 0;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <KpiCard
          testId="kpi-gross-revenue"
          label="Ricavo Lordo Annuo"
          value={fmtEUR(summary.grossRevenue)}
          subtext={`${fmtEUR(summary.saffron.total, { compact: true })} + ${fmtEUR(summary.algae.total, { compact: true })}`}
          accent="emerald"
          icon={Landmark}
        />
        <KpiCard
          testId="kpi-total-opex"
          label="Costi Operativi Annui"
          value={fmtEUR(summary.totalOpexAnnual)}
          subtext={`${fmtEUR(summary.totalOpexMonthly)} / mese`}
          accent="rose"
          icon={TrendingDown}
        />
        <KpiCard
          testId="kpi-operating-margin"
          label="EBITDA (Margine Operativo)"
          value={fmtEUR(summary.operatingMargin)}
          subtext={`${fmtPct(summary.marginPct)} sul ricavo`}
          accent={marginPositive ? "blue" : "rose"}
          icon={marginPositive ? TrendingUp : TrendingDown}
        />
        <KpiCard
          testId="kpi-breakeven-monthly"
          label="Break-Even Mensile"
          value={
            summary.breakevenMonthlyRevenue
              ? fmtEUR(summary.breakevenMonthlyRevenue)
              : "N/D"
          }
          subtext={
            summary.breakevenMonthlyRevenue
              ? "ricavo minimo per pareggiare OPEX"
              : "margine negativo: non raggiungibile"
          }
          accent="amber"
          icon={Target}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <Card className="lg:col-span-3 bg-slate-900/70 border-slate-800 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-lg font-semibold text-slate-100">
              Composizione Ricavi per Prodotto
            </h3>
            <span className="text-xs font-mono-tab text-slate-500">
              {revenueData.length} flussi attivi
            </span>
          </div>
          <div data-testid="revenue-breakdown-chart-container" className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={revenueData}
                  dataKey="value"
                  cx="50%"
                  cy="50%"
                  innerRadius={65}
                  outerRadius={110}
                  paddingAngle={2}
                  stroke="#0b0f19"
                >
                  {revenueData.map((entry, i) => (
                    <Cell key={i} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(v) => fmtEUR(v)}
                  contentStyle={{ background: "#0b0f19", border: "1px solid #1f2937", borderRadius: 8 }}
                />
                <Legend wrapperStyle={{ fontSize: 11, color: "#94a3b8" }} iconSize={8} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="lg:col-span-2 bg-slate-900/70 border-slate-800 p-6">
          <h3 className="font-display text-lg font-semibold text-slate-100 mb-4">
            Ricavi vs Costi (annuo)
          </h3>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={lotComparison} layout="vertical" margin={{ left: 20, right: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" horizontal={false} />
                <XAxis
                  type="number"
                  stroke="#64748b"
                  tick={{ fontSize: 10, fill: "#94a3b8" }}
                  tickFormatter={(v) => fmtEUR(v, { compact: true })}
                />
                <YAxis
                  type="category"
                  dataKey="name"
                  stroke="#64748b"
                  tick={{ fontSize: 10, fill: "#cbd5e1" }}
                  width={120}
                />
                <Tooltip
                  formatter={(v) => fmtEUR(v)}
                  contentStyle={{ background: "#0b0f19", border: "1px solid #1f2937", borderRadius: 8 }}
                  cursor={{ fill: "rgba(148,163,184,0.05)" }}
                />
                <Bar dataKey="value" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      <Card className="bg-slate-900/70 border-slate-800 p-6">
        <h3 className="font-display text-lg font-semibold text-slate-100 mb-4">
          Sintesi Numerica
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <StatCell label="Kg zafferano / anno" value={`${fmtNum(inputs.saffron.kg_year, 2)} kg`} />
          <StatCell
            label="Kg microalghe / anno"
            value={`${fmtNum(
              inputs.algae.chlorella.kg_year +
                inputs.algae.spirulina.kg_year +
                inputs.algae.scenedesmus.kg_year
            )} kg`}
          />
          <StatCell label="Prezzo medio zafferano" value={fmtEUR(summary.saffron.total / Math.max(inputs.saffron.kg_year, 0.01))} />
          <StatCell label="Margine %" value={fmtPct(summary.marginPct)} />
        </div>
      </Card>
    </div>
  );
};

const StatCell = ({ label, value }) => (
  <div className="border border-slate-800 rounded-lg p-3 bg-slate-950/40">
    <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
      {label}
    </div>
    <div className="mt-1 font-mono-tab text-slate-200 font-semibold">{value}</div>
  </div>
);
